import React, { useEffect, useMemo, useState } from "react";
import { Redirect, Router, Switch } from "react-router-dom";
import historyProvider from "./history";
import PrivateRoute from "./private-route";
import OnboardingPage from "../pages/private/onboarding/onboarding-page";
import EmailConfirmationPage from "../pages/private/email-confirmation/email-confirmation-page";
import SideBarsLayout from "../layouts/sidebars-layout/sidebars-layout";
import { useDispatch, useSelector } from "react-redux";
import {
  clearGlobal,
  getUser,
  updateAuthenticationToken,
} from "../store/auth/auth-actions";
import { AppState } from "../store/app-state";
import { useKeycloak } from "@react-keycloak/web";
import { Login } from "../pages/public/login";
import PublicRoute from "./public-route";
import CompanyProfile from "../pages/private/company-profile/company-profile";
import { OnboardingCompanyPage } from "../pages/private/onboarding-company";
import SelectedCandidatesPage from "../pages/private/selected-candidates/selected-candidates-page";
import TalentPoolPage from "../pages/private/talent-pool/talent-pool-page";
import CompanyTrackPage from "../pages/private/company-track/company-track-page";
import { useTranslation } from "react-i18next";
import { Events } from "../utils/constants";
import useUmamiTracking from "../hooks/use-umami-tracking";
import AccountConfirmation from "../pages/public/account-confirmation/account-confirmation";
import TalentPoolPageV2 from "../pages/private/talent-pool-v2/talent-pool-page";

const ApplicationRoutes: React.FC = () => {
  const { token, wasLogoutSolicited } = useSelector(
    (state: AppState) => state.authState
  );
  const { keycloak } = useKeycloak();
  const [logoutDone, setLogoutDone] = useState(false);
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const user = useSelector((state: any) => state.authState.user);
  const pushUmami = useUmamiTracking();

  const channel = useMemo(() => new BroadcastChannel("reload-user"), []);

  useEffect(() => {
    channel.onmessage = () => dispatch(getUser());
    return () => channel.close();
  }, [dispatch, channel]);

  useEffect(() => {
    if (!historyProvider) return;
    return historyProvider.listen((location) => {
      if (historyProvider.action === "PUSH") {
        pushUmami(Events.PAGE_VIEW, {}, user);
      }
    });
  }, [user, pushUmami]);

  useEffect(() => {
    if (keycloak.token) {
      dispatch(updateAuthenticationToken(keycloak.token));
      const locale = keycloak.tokenParsed?.locale;
      const i18nLocal = window.sessionStorage.getItem("i18n");
      const language = i18nLocal || locale || "pt-BR";
      i18n.changeLanguage(language);
      window.sessionStorage.setItem("i18n", language);
    }
  }, [keycloak.token, keycloak.tokenParsed, dispatch, i18n]);

  useEffect(() => {
    if (token) {
      dispatch(getUser());
    }
  }, [token, dispatch]);

  useEffect(() => {
    if (wasLogoutSolicited && !logoutDone) {
      setLogoutDone(true);
      keycloak.logout();
      dispatch(clearGlobal());
    }
  }, [wasLogoutSolicited, logoutDone, dispatch, keycloak]);

  return (
    <Router history={historyProvider}>
      <Switch>
        <PublicRoute path="/login" component={Login} exact />
        <PublicRoute 
            path="/account-confirmation" 
            component={AccountConfirmation} 
            exact 
        />
        <PrivateRoute
          path="/onboarding/company"
          component={OnboardingCompanyPage}
          exact
        />
        <PrivateRoute
          path="/email-confirmation"
          component={EmailConfirmationPage}
          exact
        />
        <PrivateRoute path="/onboarding" component={OnboardingPage} exact />
        <PrivateRoute
          path="/company-profile"
          layout={SideBarsLayout}
          component={CompanyProfile}
          exact
        />
        <PrivateRoute
          path="/selected-candidates"
          layout={SideBarsLayout}
          component={SelectedCandidatesPage}
          exact
        />
        <PrivateRoute
          path="/ranking"
          layout={SideBarsLayout}
          component={TalentPoolPage}
          exact
        />
         <PrivateRoute
          path="/ranking-v2"
          layout={SideBarsLayout}
          component={TalentPoolPageV2}
          exact
        />
        <PrivateRoute
          path="/track/:track"
          layout={SideBarsLayout}
          component={CompanyTrackPage}
          exact
        />
        <PrivateRoute path="*" exact>
          <Redirect to="/selected-candidates" exact />
        </PrivateRoute>
      </Switch>
    </Router>
  );
};

export default ApplicationRoutes;
